import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Quote, Globe, TrendingUp, Package } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function Home() {
  const [bestsellers, setBestsellers] = useState([]);
  const [shirts, setShirts] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { bestseller: true } }).then((r) => setBestsellers(r.data.slice(0, 8)));
    api.get("/products", { params: { category: "Apparel" } }).then((r) => setShirts(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/440b4a7a-6970-4e79-9d3d-7e44b0363852/images/30a6001981bccb00397c02742067aebf63313ea9778f4e9aae2f9e230899560c.jpeg"
            alt="Blue marlin leaping free from the ocean at golden hour"
            className="w-full h-full object-cover"
            data-testid="hero-marlin-bg"
          />
          <div className="absolute inset-0 bg-black/35" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-28 sm:py-44">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <p className="overline text-white mb-4">Hunting & Fishing Apparel · Worldwide shipping</p>
            <h1 className="font-display text-5xl sm:text-7xl font-black tracking-tighter leading-none text-white mb-6">
              Built for the<br />
              <span className="text-brand">field & the water.</span>
            </h1>
            <p className="text-lg text-white/85 mb-8 max-w-lg">
              Premium hunting and fishing shirts — camo, flannel and vented performance styles built for long days outdoors.
              Shipped worldwide with taxes handled. Use <span className="font-mono font-bold text-white">WELCOME10</span> for 10% off.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/shop?type=shirts" className="btn-primary" data-testid="hero-shop-btn">
                Shop shirts <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact"
                className="btn-secondary bg-white/15 backdrop-blur-sm !text-white font-bold border-2 border-white hover:!bg-white hover:!text-ink"
                data-testid="hero-sell-btn"
              >
                Sell with us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stat strip */}
      <section className="border-b border-ink/10 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-ink/10">
          {[
            { icon: TrendingUp, t: "Our picks", d: "Hand-selected best-sellers" },
            { icon: Globe, t: "Tax included at checkout", d: "GST/VAT shown upfront — no surprises" },
            { icon: Package, t: "Fast fulfilment", d: "Free NZ & AU shipping over $75" },
          ].map((s) => (
            <div key={s.t} className="flex items-center gap-4 py-8 px-2 sm:px-8">
              <s.icon className="text-brand" size={28} />
              <div>
                <p className="font-display font-bold">{s.t}</p>
                <p className="text-sm text-muted-foreground">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Hunting & Fishing Shirts */}
      <section className="bg-secondary py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="overline mb-2">Field & Water</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Hunting & fishing shirts</h2>
            </div>
            <Link to="/shop?type=shirts" className="text-sm font-bold hover:text-brand flex items-center gap-1" data-testid="view-shirts-link">
              View all shirts <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5" data-testid="shirts-grid">
            {shirts.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Best sellers */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline mb-2">Trending now</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Best sellers</h2>
          </div>
          <Link to="/shop" className="text-sm font-bold hover:text-brand flex items-center gap-1" data-testid="view-all-link">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {bestsellers.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="bg-secondary py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="overline mb-2">Browse</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-10">Shop by category</h2>
          <div className="flex flex-wrap gap-3">
            {categories.map((c) => (
              <Link
                key={c}
                to={`/shop?category=${encodeURIComponent(c)}`}
                data-testid={`category-${c.toLowerCase()}`}
                className="border border-ink px-6 py-3 font-bold uppercase tracking-wide text-sm hover:bg-ink hover:text-white transition-colors"
              >
                {c}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CEO message */}
      <section className="relative bg-ink text-white py-24 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <Quote className="text-brand mx-auto mb-6" size={40} />
          <p className="font-display text-2xl sm:text-3xl font-bold leading-snug mb-6">
            "We built Go Big Forever to bring the world's best products to every doorstep in New Zealand and
            Australia — with honest pricing, fast shipping, and support that actually helps."
          </p>
          <p className="overline">W. Johnson</p>
          <p className="text-sm text-white/60">Founder & CEO, Go Big Forever</p>
        </div>
      </section>
    </div>
  );
}
